import { useState, useEffect } from "react";
import { Trash2, BookOpen, Calendar, Cloud, Sun, CloudRain, CloudLightning, X, ExternalLink } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import { useLanguage } from "../../context/LanguageContext";
import api from "../../api/axios";

export default function DiaryHistory({ limit = Infinity }) {
    const { t } = useLanguage();
    const [entries, setEntries] = useState([]);
    const [selectedEntry, setSelectedEntry] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [showArchive, setShowArchive] = useState(false);

    useEffect(() => {
        loadEntries();
    }, []);

    const loadEntries = async () => {
        setIsLoading(true);
        try {
            const { data } = await api.get("/diary");
            setEntries(data || []);
        } catch (e) {
            console.error("Failed to fetch diary entries", e);
            toast.error(t('diary_load_error') || "Could not load your diary entries.");
        } finally {
            setIsLoading(false);
        }
    };

    const handleDelete = async (id, e) => {
        e.stopPropagation();
        if (window.confirm(t('diary_del_confirm'))) {
            try {
                await api.delete(`/diary/${id}`);
                setEntries(prev => prev.filter(entry => entry.id !== id));
                toast.success(t('diary_del_success'));
                if (selectedEntry?.id === id) setSelectedEntry(null);
            } catch (error) {
                console.error("Delete error:", error);
                toast.error(t('diary_del_error'));
            }
        }
    };

    const getMoodIcon = (moodLabel) => {
        switch (moodLabel) {
            case "Happy": return <Sun className="w-5 h-5 text-amber-500" />;
            case "Neutral": return <Cloud className="w-5 h-5 text-gray-400" />;
            case "Sad": return <CloudRain className="w-5 h-5 text-blue-500" />;
            case "Anxious": return <CloudLightning className="w-5 h-5 text-purple-500" />;
            default: return <BookOpen className="w-5 h-5 text-amber-600" />;
        }
    };

    if (isLoading) {
        return (
            <div className="flex justify-center items-center h-48">
                <div className="w-8 h-8 border-4 border-amber-600/20 border-t-amber-600 animate-spin rounded-full" />
            </div>
        );
    }

    if (entries.length === 0) {
        return (
            <div className="text-center p-10 bg-gray-50 dark:bg-gray-800 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700">
                <BookOpen className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                <h3 className="text-lg font-display font-bold text-gray-600 dark:text-gray-400">{t('diary_empty')}</h3>
                <p className="text-xs text-gray-500 mt-2 font-medium tracking-wide">{t('diary_empty_sub')}</p>
            </div>
        );
    }

    const displayEntries = entries.slice(0, limit);
    const hasMore = entries.length > limit;

    return (
        <div className="space-y-6">
            <div className="flex justify-between items-end">
                <h2 className="text-2xl font-display font-bold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                    <BookOpen className="w-6 h-6 text-amber-600" />
                    {t('diary_title')}
                </h2>
                {hasMore && (
                    <button 
                        onClick={() => setShowArchive(true)}
                        className="text-xs font-bold text-amber-600 hover:text-amber-700 underline flex items-center gap-1 uppercase tracking-widest"
                    >
                        {t('diary_view_all')} <ExternalLink className="w-3 h-3" />
                    </button>
                )}
            </div>

            <div className={`grid grid-cols-1 ${limit > 2 ? "md:grid-cols-2 lg:grid-cols-3" : "md:grid-cols-2"} gap-4`}>
                {displayEntries.map((entry) => (
                    <motion.div
                        key={entry.id}
                        layoutId={`card-${entry.id}`}
                        onClick={() => setSelectedEntry(entry)}
                        className="group relative bg-white dark:bg-gray-800 p-5 rounded-2xl border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md transition-all cursor-pointer overflow-hidden"
                    >
                        {/* Always visible on mobile, hover on desktop */}
                        <div className="absolute top-2 right-2 p-2 opacity-100 md:opacity-0 md:group-hover:opacity-100 transition-opacity z-10">
                            <button
                                onClick={(e) => handleDelete(entry.id, e)}
                                className="p-2 bg-red-50 dark:bg-red-900/20 text-red-500 rounded-xl hover:bg-red-100 dark:hover:bg-red-900/40 shadow-sm"
                                title={t('diary_del_tooltip') || "Delete Entry"}
                            >
                                <Trash2 className="w-4 h-4" />
                            </button>
                        </div>

                        <div className="flex items-center gap-2 mb-3 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                            <Calendar className="w-3 h-3 text-amber-600/50" />
                            {new Date(entry.createdAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                        </div>

                        <div className="mb-3">
                            {entry.mood && (
                                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-lg bg-amber-50 dark:bg-amber-900/20 text-amber-700 dark:text-amber-300 text-[10px] font-bold uppercase tracking-wide mb-3">
                                    {getMoodIcon(entry.mood)}
                                    {t('mood_' + entry.mood.toLowerCase()) || entry.mood}
                                </div>
                            )}
                            <h4 className="font-serif italic text-gray-500 dark:text-gray-400 text-sm line-clamp-2 mb-2 leading-relaxed">
                                "{entry.prompt || "Dear Diary..."}"
                            </h4>
                        </div>

                        <p className="text-gray-800 dark:text-gray-200 line-clamp-3 text-sm leading-relaxed font-serif">
                            {entry.content}
                        </p>
                    </motion.div>
                ))}
            </div>

            {/* Modals Container */}
            <AnimatePresence>
                {/* 1. Full View Modal */}
                {selectedEntry && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[200] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
                        onClick={() => setSelectedEntry(null)}
                    >
                        <motion.div
                            layoutId={`card-${selectedEntry.id}`}
                            className="bg-[#fdfbf7] dark:bg-gray-800 w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-3xl shadow-2xl p-6 md:p-10 relative border-l-8 border-amber-800 dark:border-l-gray-900"
                            onClick={(e) => e.stopPropagation()}
                        >
                            <button
                                onClick={() => setSelectedEntry(null)}
                                className="absolute top-6 right-6 text-gray-400 hover:text-gray-600 dark:text-gray-500 dark:hover:text-gray-300 p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full transition-colors"
                            >
                                <X className="w-6 h-6" />
                            </button>

                            <div className="flex items-center gap-4 mb-8 border-b border-gray-200 dark:border-gray-700 pb-6">
                                <div className="p-4 bg-amber-100 dark:bg-amber-900/30 rounded-2xl shadow-inner">
                                    {getMoodIcon(selectedEntry.mood)}
                                </div>
                                <div>
                                    <div className="text-[10px] uppercase font-bold tracking-widest text-gray-400 mb-1">
                                        {new Date(selectedEntry.createdAt).toLocaleString(undefined, { dateStyle: 'full', timeStyle: 'short' })}
                                    </div>
                                    <div className="font-display font-bold text-xl text-gray-900 dark:text-gray-100 italic">
                                        {t('mood_' + (selectedEntry.mood || 'neutral').toLowerCase()) || selectedEntry.mood}
                                    </div>
                                </div>
                            </div>

                            <div className="mb-8 p-5 bg-white dark:bg-gray-700/30 rounded-2xl border border-amber-100 dark:border-gray-600 shadow-sm">
                                <span className="text-[10px] font-black text-amber-600 dark:text-amber-400 uppercase tracking-[0.2em] block mb-3">{t('diary_reflecting_on')}</span>
                                <p className="font-serif italic text-base md:text-lg text-gray-700 dark:text-gray-300 leading-relaxed">
                                    "{selectedEntry.prompt}"
                                </p>
                            </div>

                            <div className="prose dark:prose-invert max-w-none text-gray-800 dark:text-gray-200 font-serif text-lg leading-relaxed whitespace-pre-wrap">
                                {selectedEntry.content}
                            </div>
                        </motion.div>
                    </motion.div>
                )}

                {/* 2. Archive Modal */}
                {showArchive && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        className="fixed inset-0 z-[150] flex items-center justify-center bg-white dark:bg-gray-950 p-0 md:p-6"
                    >
                        <div className="w-full h-full max-w-5xl bg-[#fdfbf7] dark:bg-gray-950 md:rounded-3xl shadow-2xl flex flex-col overflow-hidden border border-gray-100 dark:border-gray-800">
                            {/* Header */}
                            <div className="p-6 md:p-8 border-b border-gray-200 dark:border-gray-800 flex justify-between items-center bg-white dark:bg-gray-900">
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 bg-amber-600 text-white rounded-xl flex items-center justify-center shadow-lg">
                                        <BookOpen className="w-5 h-5" />
                                    </div>
                                    <div>
                                        <h2 className="text-xl md:text-2xl font-display font-bold text-gray-900 dark:text-gray-100 italic">{t('diary_archive_title')}</h2>
                                        <p className="text-xs text-gray-500 font-medium">{t('diary_memories_count', { count: entries.length })}</p>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => setShowArchive(false)}
                                    className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full transition-colors"
                                >
                                    <X className="w-7 h-7 text-gray-400" />
                                </button>
                            </div>

                            {/* Scroll Area */}
                            <div className="flex-1 overflow-y-auto p-6 md:p-10 space-y-8 bg-amber-50/20 dark:bg-gray-950">
                                <DiaryHistory limit={Infinity} />
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
